import './ProfilePhotosView.css'

import { useEffect, useRef, useState, useLayoutEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { session, profile, photos } from '../store'
import { calculateCoverLayout } from '../utilities/cover-image'

import { useRequireAuthentication } from '../hooks/useRequireAuthentication'
import { useRequireOnboarding } from '../hooks/useRequireOnboarding'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { usePhotoObjectUrl } from '../hooks/usePhotoObjectUrl'
import { Button } from '../components/Button'

type PhotoCardProps = {
  photo: photos.Photo
  isMain: boolean
  onSetMain?: () => void | Promise<void>
  onDelete: () => void | Promise<void>
}

function PhotoCard(props: PhotoCardProps) {
  const { photo, isMain, onSetMain, onDelete } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(null)

  useLayoutEffect(() => {
    if (!containerRef.current) return

    const updateSize = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      setContainerSize({ width: rect.width, height: rect.height })
    }

    updateSize()

    const observer = new ResizeObserver(updateSize)
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'green'
      case 'rejected':
        return 'red'
      case 'processing':
        return 'blue'
      case 'pending':
      default:
        return 'yellow'
    }
  }

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1)
  }

  const photoUrl = usePhotoObjectUrl(photo.id, 400)

  const layout =
    containerSize && photo.imageWidth && photo.imageHeight
      ? calculateCoverLayout(
          photo.imageWidth,
          photo.imageHeight,
          containerSize.width,
          containerSize.height,
        )
      : null

  return (
    <div className="profile-photos-view__photo-card">
      <div ref={containerRef} className="profile-photos-view__photo-container">
        {photoUrl && (
          <img
            src={photoUrl}
            alt="Profile photo"
            className="profile-photos-view__photo-image"
            style={layout?.imageStyle}
          />
        )}
        {isMain && <div className="profile-photos-view__main-marker">Main</div>}
        <div
          className="profile-photos-view__photo-status"
          data-status={getStatusColor(photo.status)}
        >
          {getStatusLabel(photo.status)}
        </div>
        {layout &&
          photo.faces
            ?.filter((face) => face.confidence >= 0.7)
            .map((face, index) => (
              <div
                key={index}
                className="profile-photos-view__face-box"
                style={layout.scaleFaceBox(face.boundingBox)}
              />
            ))}
      </div>

      <div className="profile-photos-view__photo-info">
        <div className="profile-photos-view__photo-date">
          Uploaded {new Date(photo.createdAt).toLocaleDateString()}
        </div>

        <div className="profile-photos-view__photo-actions">
          {onSetMain && <Button kind="secondary" text="Set as Main" onClick={onSetMain} />}
          <Button
            className="profile-photos-view__delete-button"
            kind="destructive"
            text="Delete"
            onClick={onDelete}
          />
        </div>
      </div>
    </div>
  )
}

export function ProfilePhotosView() {
  useRequireAuthentication()
  useRequireOnboarding()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [, setBreadcrumbs] = useBreadcrumbs()

  const authStatus = useAtomValue(session.sessionStatusAtom)
  const currentProfile = useAtomValue(profile.profileAtom)
  const profilePhotos = useAtomValue(photos.photosAtom)

  const fetchMyProfile = useSetAtom(profile.fetchMyProfileAtom)
  const fetchPhotos = useSetAtom(photos.fetchPhotosAtom)
  const uploadPhoto = useSetAtom(photos.uploadPhotoAtom)
  const deletePhoto = useSetAtom(photos.deletePhotoAtom)
  const setMainPhoto = useSetAtom(profile.setMainPhotoAtom)

  useEffect(() => {
    setBreadcrumbs([{ label: 'Your Profile', path: '/profile' }, { label: 'Photos' }])
  }, [setBreadcrumbs])

  useEffect(() => {
    if (authStatus !== 'authenticated') return

    fetchMyProfile().then((fetchedProfile) => {
      if (fetchedProfile) {
        fetchPhotos(fetchedProfile.id)
      }
    })
  }, [authStatus, fetchMyProfile, fetchPhotos])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !currentProfile) return

    await uploadPhoto(currentProfile.id, file)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDeletePhoto = async (photoId: string) => {
    await deletePhoto(photoId)
  }

  const handleSetMainPhoto = async (photoId: string) => {
    const result = await setMainPhoto(photoId)
    if (result.success && currentProfile) {
      await fetchPhotos(currentProfile.id)
    } else {
      console.error('Failed to set main photo:', result.error)
    }
  }

  if (authStatus !== 'authenticated' || !currentProfile) {
    return (
      <div className="profile-photos-view">
        <div className="profile-photos-view__loading">Loading...</div>
      </div>
    )
  }

  // Backend returns photos already sorted: main first, then approved in order, then others
  const approvedPhotos = profilePhotos.filter((p) => p.status === 'approved')
  const pendingPhotos = profilePhotos.filter((p) => p.status !== 'approved')

  return (
    <div className="profile-photos-view">
      <div className="profile-photos-view__upload-section">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
        <Button kind="primary" text="Upload Photo" onClick={handleUploadClick} />
        <p className="profile-photos-view__upload-hint">
          You can upload up to 6 photos. All photos are reviewed before being made public.
        </p>
      </div>

      <div className="profile-photos-view__photos-list">
        {profilePhotos.length === 0 ? (
          <div className="profile-photos-view__empty">
            <p>You haven't uploaded any photos yet.</p>
            <p>Upload your first photo to get started!</p>
          </div>
        ) : (
          <>
            {approvedPhotos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                isMain={photo.id === currentProfile?.mainPhotoId}
                onSetMain={
                  photo.id !== currentProfile?.mainPhotoId
                    ? () => handleSetMainPhoto(photo.id)
                    : undefined
                }
                onDelete={() => handleDeletePhoto(photo.id)}
              />
            ))}

            {pendingPhotos.map((photo) => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                isMain={false}
                onDelete={() => handleDeletePhoto(photo.id)}
              />
            ))}
          </>
        )}
      </div>
    </div>
  )
}
